// components/InfoTip.jsx — تلميح معلومات موحّد (تبويب/تحويم)، الجولة
// الختامية 2026-10، البند 5. يستبدل خاصية title= الأصلية (لا تعمل جيداً
// باللمس على iPad/الموبايل — المتصفح لا يُظهرها إطلاقاً باللمس، ولا تحترم
// المساحة الآمنة). زر التشغيل بهدف لمس ≥44×44px دوماً. يُركَّب عبر
// Portal (مستوى tooltip، الأعلى في سلّم lib/zIndex.js) فلا يُحتجَز أبداً
// خلف عنصر عائم آخر. ممنوع أي title= مستقل جديد للمعلومات بعد اليوم —
// يفرضه tests/test_zindex_title_lint_guards.py.
//
// إصلاح iPad حقيقي (2026-10-09، تدقيق لمس حقيقي عبر Playwright has_touch+tap):
// ترتيب الأحداث الفعلي عند اللمس هو pointerdown→touchstart→pointerup→
// touchend→mouseenter→mousedown→mouseup→click→mouseleave (المتصفح يُولِّد
// أحداث فأرة تركيبية بعد اللمس). كان onMouseEnter يفتح الفقاعة (show())
// قبل click، فيقرأ onClick حالة open=true المُحدَّثة ويُغلقها فوراً
// (hide()) — نفس اللمسة تفتح ثم تغلق خلال جزء من الثانية فلا تظهر أبداً.
// الإصلاح: onPointerEnter/onPointerLeave يتحققان من e.pointerType==="mouse"
// فلا يستجيبان لأحداث اللمس إطلاقاً؛ onClick يبقى المُحرِّك الوحيد على
// اللمس (بلا منافس)، فلا تضارب.
//
// سبب ثانٍ حقيقي (تشويه بصري، لا علاقة بفتح/إغلاق الفقاعة): قاعدة عامة في
// index.css (v119.235، تقريباً سطر 3537 — "آخر الملف عمداً يفوز في
// التسلسل") تطبّق max-width:94vw !important على أي
// div[style*="position:fixed"] > div[style*="max-width"] ظنّاً أنه مودال،
// فتحوّل فقاعتنا (260px) إلى ~783px على عرض iPad 834px (نفس بنية DOM:
// خلفية ثابتة > صندوق بـmax-width). القاعدة نفسها تحمل مَخرجاً مُعدّاً
// سلفاً :not([data-no-modal-clamp])؛ أُضيف data-no-modal-clamp على صندوق
// الفقاعة أدناه فلا تلتقطه قاعدة المودال العامة بعد اليوم.
// A3 (البند 3د، 2026-10-12) — على الأجهزة اللمسية (pointer:coarse أو
// maxTouchPoints>0) تُفتح لوحة سفلية بعرض الشاشة كاملاً (bottom sheet) عبر
// Portal نفسه، مع تعتيم خلفي وزر إغلاق صريح 44×44 — بدل الفقاعة الصغيرة
// القريبة من الزر (كانت تضيق على المساحة الآمنة/تُقرَأ بصعوبة على iPad).
// الفتح لا يزال بـclick فقط على اللمس (onPointerEnter/Leave أعلاه يتجاهلان
// غير الفأرة بالفعل). الحاسوب (الفأرة) كما هو تماماً: فقاعة صغيرة قرب الزر.
import React, { useRef, useState, useLayoutEffect } from "react";
import { Info, X } from "lucide-react";
import { Portal } from "./Portal";
import { api } from "../lib/api";

const EDGE_MARGIN = 8;

function isTouchDevice() {
  if (typeof window === "undefined") return false;
  return (window.matchMedia && window.matchMedia("(pointer: coarse)").matches)
    || (navigator.maxTouchPoints || 0) > 0;
}

// تصحيح A3 (الجولة الرابعة، 2026-10-12) — دليل جهاز حقيقي (جدول ضغطات ⓘ):
// اللوحة تُفتح (event=opened) لكنها "فوق الكل؟ لا" لكل علامات
// BranchCustomerCards تقريباً، بينما علامة واحدة («ضيف جديد») نجحت. لا
// علاقة بـz-index (رُفع مسبقاً فوق كل الطبقات) — العلّة توقيت: التسلسل
// الحقيقي الموثَّق أعلاه (2026-10-09) هو pointerdown→touchstart→pointerup→
// touchend→mouseenter→mousedown→mouseup→click→mouseleave. onPointerEnter
// كان يتحقق فقط من e.pointerType==="mouse" ليقرر الاستجابة — لكن
// mouseenter/pointerenter التركيبي الذي يُولِّده WebKit بعد لمس حقيقي
// *يحمل أيضاً* pointerType="mouse" (فهو MouseEvent تركيبي، لا علاقة
// لنوعه الظاهري بنيّة المستخدم الفعلية). فيستجيب show() لهذا الحدث
// التركيبي فيفتح اللوحة؛ ثم click الحقيقي يصل فيقرأ open=true (المحدَّثة
// للتو) فيُنفِّذ hide() ظناً أنه toggle — تُفتح ثم تُغلَق خلال نفس اللمسة
// قبل اكتمال الرسم المرئي، فيقرأ elementFromPoint طبقة أخرى تحتها.
// الفرق العشوائي بين العلامات هو توقيت تنفيذ المتصفح للتسلسل التركيبي لكل
// عنصر على حدة — سباق حقيقي لا خطأ في عنصر بعينه. الإصلاح: تجاهل أي
// mouseenter/pointerenter "فأرة" يصل خلال TOUCH_SYNTHETIC_GUARD_MS من آخر
// لمسة حقيقية فعلية (touchstart/pointerdown بنوع غير فأرة) على نفس
// العلامة — قيمة تقنية لمعالجة سباق أحداث المتصفح، لا قاعدة عمل بالثواني.
const TOUCH_SYNTHETIC_GUARD_MS = 500;

function _elementDescriptor(el) {
  if (!el) return "";
  const tag = el.tagName ? el.tagName.toLowerCase() : "";
  const cls = typeof el.className === "string" ? el.className.slice(0, 60) : "";
  const tid = el.getAttribute ? (el.getAttribute("data-testid") || "") : "";
  return `${tag}${cls ? "." + cls.replace(/\s+/g, ".") : ""}${tid ? `[data-testid=${tid}]` : ""}`;
}

function _sendUiEvent(tipId, event, extra) {
  try {
    api.post("/ui-events/infotip", {
      tip_id: tipId || "anonymous",
      event,
      pointer_type: extra?.pointerType || "",
      max_touch_points: navigator.maxTouchPoints || 0,
      user_agent: (navigator.userAgent || "").slice(0, 160),
      opened_on_top: extra?.openedOnTop,
      // تصحيح A3 (الجولة الرابعة، 2026-10-12) — بند (ب): تفاصيل كافية
      // للتشخيص بلا الحاجة لتخمين لاحق.
      top_element: extra?.topElement || "",
      top_element_zindex: extra?.topElementZIndex ?? null,
      bubble_rect: extra?.bubbleRect || null,
      bubble_under_portal_root: extra?.bubbleUnderPortalRoot,
      recheck_300ms_on_top: extra?.recheck300msOnTop,
      close_reason: extra?.closeReason || "",
      open_duration_ms: extra?.openDurationMs ?? null,
    }).catch(() => {});
  } catch { /* تجاهل — تشخيص لا يجب أن يكسر أي تفاعل */ }
}

export function InfoTip({ text, children, testId }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const bubbleRef = useRef(null);
  const touchRef = useRef(false);
  // تصحيح A3 (الجولة الرابعة، 2026-10-12) — طابع آخر لمسة حقيقية فعلية
  // (غير فأرة)، وطابع لحظة الفتح الفعلية (لحساب المدة وحماية الإغلاق
  // المبكر من الأحداث التركيبية التالية لنفس اللمسة).
  const lastRealTouchAtRef = useRef(0);
  const openedAtRef = useRef(0);
  const openRef = useRef(false);
  useLayoutEffect(() => { openRef.current = open; }, [open]);

  const show = () => {
    touchRef.current = isTouchDevice();
    openedAtRef.current = Date.now();
    if (touchRef.current) { setOpen(true); return; } // اللوحة السفلية بلا حساب موضع
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const vw = window.innerWidth;
    let left = Math.max(EDGE_MARGIN, r.left);
    if (left > vw - EDGE_MARGIN) left = Math.max(EDGE_MARGIN, vw - EDGE_MARGIN);
    setPos({ top: r.bottom + 6, left, triggerTop: r.top, triggerBottom: r.bottom });
    setOpen(true);
  };
  // تصحيح A3 (الجولة الرابعة) — hide(reason) بند (ب+د): "outside-click"
  // ضمن نافذة الحماية بعد الفتح تُتجاهَل (غالباً صدى الحدث التركيبي نفسه
  // الذي فتحها لتوّه)؛ الإغلاق الصريح (زر الإغلاق/toggle الزر نفسه) يعمل
  // دوماً بصرف النظر عن التوقيت. يُسجَّل حدث "closed" فقط عند إغلاق فعلي.
  const hide = (reason = "unspecified") => {
    if (reason === "outside-click" && Date.now() - openedAtRef.current < TOUCH_SYNTHETIC_GUARD_MS) {
      return;
    }
    if (open) {
      _sendUiEvent(testId, "closed", {
        closeReason: reason,
        openDurationMs: Date.now() - openedAtRef.current,
      });
    }
    setOpen(false);
  };

  // تصحيح A3 (الجولة الرابعة، 2026-10-12) — السبب الفعلي الجديد: مهما
  // كان e.pointerType="mouse" فهذا لا يعني فأرة حقيقية — WebKit يولّد
  // pointerenter/mouseenter تركيبياً بعد لمس حقيقي بهذا النوع تحديداً.
  // التجاهل الصحيح: فأرة *و* لم تحدث لمسة حقيقية خلال النافذة الأخيرة.
  const handlePointerEnter = (e) => {
    if (e.pointerType !== "mouse") return;
    if (Date.now() - lastRealTouchAtRef.current < TOUCH_SYNTHETIC_GUARD_MS) return;
    show();
  };
  const handlePointerLeave = (e) => {
    if (e.pointerType !== "mouse") return;
    if (Date.now() - lastRealTouchAtRef.current < TOUCH_SYNTHETIC_GUARD_MS) return;
    hide("mouse-leave");
  };
  const handleClick = (e) => {
    e.stopPropagation();
    if (open) hide("toggle-click"); else show();
  };
  // تصحيح A3 (الجولة الثانية، 2026-10-12) — نبض أول فور بدء اللمس، قبل أي
  // منطق لاحق؛ لو وصل هذا للخادم بلا "opened" مقابلة خلال ١٠ث فاللمس يصل
  // للمتصفح لكن لا يتحوّل click (مشكلة بوابة حقيقية، لا React).
  // الجولة الرابعة — يُسجِّل أيضاً طابع اللمسة الحقيقية لحماية pointerenter.
  const handlePointerDownNative = (e) => {
    if (e.pointerType === "mouse") return;
    lastRealTouchAtRef.current = Date.now();
    _sendUiEvent(testId, "pointerdown", { pointerType: e.pointerType });
  };

  // تصحيح A3 (الجولة الرابعة، بند ب) — تمرير الصفحة خلف الفقاعة (الحاسوب
  // فقط؛ اللوحة السفلية على اللمس تُغطّي الشاشة كاملة فلا تمرير خلفها
  // أصلاً) يُغلقها صراحة بسبب "scroll" بدل تركها بموضع قديم خاطئ.
  useLayoutEffect(() => {
    if (!open || touchRef.current) return;
    const onScroll = () => hide("scroll");
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    return () => window.removeEventListener("scroll", onScroll, { capture: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);



  // تقييد الفقاعة فعلياً داخل حدود الشاشة (عرض وارتفاع) بعد قياسها الحقيقي
  // (بُعدها يتغيّر بطول النص)؛ تُعكَس فوق العلامة إن لم تكفِ المساحة تحتها.
  useLayoutEffect(() => {
    if (!open || !pos || !bubbleRef.current || touchRef.current) return;
    const rect = bubbleRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let { top, left } = pos;
    let changed = false;
    if (left + rect.width > vw - EDGE_MARGIN) {
      left = Math.max(EDGE_MARGIN, vw - EDGE_MARGIN - rect.width);
      changed = true;
    }
    if (top + rect.height > vh - EDGE_MARGIN) {
      const above = pos.triggerTop - rect.height - 6;
      top = above >= EDGE_MARGIN ? above : Math.max(EDGE_MARGIN, vh - EDGE_MARGIN - rect.height);
      changed = true;
    }
    if (changed && (left !== pos.left || top !== pos.top)) {
      setPos((p) => (p ? { ...p, top, left } : p));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pos?.top, pos?.left]);

  // تصحيح A3 (الجولة الثانية، 2026-10-12؛ وُسِّع في الجولة الرابعة بند ب)
  // — بعد نجاح show() والرسم فعلياً على اللمس: تحقق elementFromPoint في
  // مركز اللوحة = اللوحة نفسها، مع هوية العنصر الحاجب وz-index المحسوب له
  // إن وُجد، ورصد الحاوية (داخل #app-portal-root فعلاً؟)، وقياس ثانٍ بعد
  // 300ms (هل أُغلقت بالفعل بحلول وقته — يكشف سباق الإغلاق المبكر مباشرة).
  useLayoutEffect(() => {
    if (!open || !touchRef.current || !bubbleRef.current) return;
    const bubbleEl = bubbleRef.current;
    const measure = () => {
      const rect = bubbleEl.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const topEl = document.elementFromPoint(cx, cy);
      const onTop = !!(topEl && bubbleEl.contains(topEl));
      return { onTop, topEl, rect };
    };
    const first = measure();
    const portalRoot = document.getElementById("app-portal-root");
    _sendUiEvent(testId, "opened", {
      openedOnTop: first.onTop,
      topElement: first.onTop ? "" : _elementDescriptor(first.topEl),
      topElementZIndex: first.onTop || !first.topEl
        ? null : window.getComputedStyle(first.topEl).zIndex,
      bubbleRect: {
        top: Math.round(first.rect.top), left: Math.round(first.rect.left),
        width: Math.round(first.rect.width), height: Math.round(first.rect.height),
      },
      bubbleUnderPortalRoot: !!(portalRoot && portalRoot.contains(bubbleEl)),
    });
    const recheckTimer = setTimeout(() => {
      // تحقَّق أن اللوحة ما زالت موجودة فعلياً في DOM قبل القياس الثاني —
      // لو أُغلِقت (unmount) فالقياس الثاني غير ذي معنى هنا (يُسجَّله حدث
      // closed بسببه الخاص أدناه).
      if (!document.body.contains(bubbleEl)) return;
      const second = measure();
      _sendUiEvent(testId, "opened", { recheck300msOnTop: second.onTop });
    }, 300);
    return () => clearTimeout(recheckTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // تصحيح A3 (الجولة الرابعة) — حدث "closed" عند إلغاء تركيب اللوحة بسبب
  // تفكيك المكوّن نفسه (تنقّل/إزالة من الشجرة) أثناء كونها مفتوحة، لا سبب
  // تفاعلي صريح آخر.
  useLayoutEffect(() => () => {
    if (openRef.current) {
      _sendUiEvent(testId, "closed", {
        closeReason: "unmount",
        openDurationMs: Date.now() - openedAtRef.current,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  return (
    <span style={{ position: "relative", display: "inline-flex" }}>
      <button
        type="button"
        ref={btnRef}
        data-testid={testId || "info-tip-trigger"}
        aria-label={text}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        onPointerDown={handlePointerDownNative}
        onClick={handleClick}
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 44, height: 44, minWidth: 44, minHeight: 44,
          background: "transparent", border: "none", cursor: "help", color: "inherit",
        }}
      >
        {children || <Info size={14} />}
      </button>
      {open && touchRef.current && (
        <Portal level="tooltip">
          <div data-testid={testId ? `${testId}-overlay` : "info-tip-overlay"}
               onClick={() => hide("outside-click")}
               style={{
                 position: "fixed", inset: 0, background: "rgba(17,12,8,0.45)",
               }}>
            <div
              ref={bubbleRef}
              data-testid={testId ? `${testId}-bubble` : "info-tip-bubble"}
              data-no-modal-clamp
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "fixed", insetInlineStart: 0, insetInlineEnd: 0, bottom: 0,
                width: "100%", maxWidth: "100%",
                background: "#1f2937", color: "#fff",
                fontSize: 14, lineHeight: 1.7, padding: "16px 18px",
                borderStartStartRadius: 16, borderStartEndRadius: 16,
                boxShadow: "0 -6px 24px rgba(0,0,0,0.3)",
                paddingBottom: "calc(18px + env(safe-area-inset-bottom, 0px))",
              }}
              dir="rtl"
            >
              <button
                type="button"
                data-testid={testId ? `${testId}-close` : "info-tip-close"}
                aria-label="إغلاق"
                onClick={() => hide("close-button")}
                style={{
                  position: "absolute", top: 8, insetInlineEnd: 8,
                  width: 44, height: 44, minWidth: 44, minHeight: 44,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: "rgba(255,255,255,0.1)", border: "none",
                  borderRadius: 999, color: "#fff", cursor: "pointer",
                }}
              >
                <X size={18} />
              </button>
              <div style={{ paddingInlineEnd: 40 }}>{text}</div>
            </div>
          </div>
        </Portal>
      )}
      {open && pos && !touchRef.current && (
        <Portal level="tooltip">
          <div onClick={() => hide("outside-click")} style={{ position: "fixed", inset: 0 }}>
            <div
              ref={bubbleRef}
              data-testid={testId ? `${testId}-bubble` : "info-tip-bubble"}
              data-no-modal-clamp
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "fixed", top: pos.top, left: pos.left,
                maxWidth: 260, background: "#1f2937", color: "#fff",
                fontSize: 12, lineHeight: 1.5, padding: "8px 10px",
                borderRadius: 8, boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
                paddingBottom: "calc(8px + env(safe-area-inset-bottom, 0px))",
              }}
              dir="rtl"
            >
              {text}
            </div>
          </div>
        </Portal>
      )}
    </span>
  );
}
